import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useDebugDeleteDocumentsMutation,
  useDebugDocumentsQuery,
} from 'iadocs/api/debug'
import { DebugMultiSelect } from 'iadocs/components/DebugMultiSelect'
import { DebugPagination } from 'iadocs/components/DebugPagination'
import {
  DATE_OPERATOR_OPTIONS,
  DOCUMENT_PER_PAGE_OPTIONS,
  NOM_DOCUMENT_OPTIONS,
} from 'iadocs/const/debugOptions'
import { getDebugErrorMessage } from 'iadocs/debug/display'
import {
  readListParam,
  readPositiveInt,
  readTextParam,
  useDebugHeader,
} from 'iadocs/debug/search'
import { DebugDocumentFilters } from 'iadocs/interfaces/IDebug'

const DEFAULT_PER_PAGE = 100

const hasActiveFilter = (filters: DebugDocumentFilters) => {
  return (
    Boolean(filters.hash) ||
    filters.nomDocuments.length > 0 ||
    Boolean(filters.dateOperator && filters.dateMonth)
  )
}

export const DebugDocumentsPage = () => {
  useDebugHeader()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [notice, setNotice] = useState<{
    level: 'success' | 'warning' | 'error'
    message: string
  } | null>(null)

  const page = readPositiveInt(searchParams.get('page'), 1)
  const perPage = readPositiveInt(
    searchParams.get('perPage'),
    DEFAULT_PER_PAGE,
    100
  )
  const filters = useMemo<DebugDocumentFilters>(
    () => ({
      hash: readTextParam(searchParams, 'hash'),
      nomDocuments: readListParam(searchParams, 'nomDocuments'),
      dateOperator: readTextParam(searchParams, 'dateOperator'),
      dateMonth: readTextParam(searchParams, 'dateMonth'),
    }),
    [searchParams]
  )

  const [draft, setDraft] = useState(filters)

  useEffect(() => {
    setDraft(filters)
    setSelectedIds([])
  }, [filters])

  const { data, isLoading, isError, error, isFetching } =
    useDebugDocumentsQuery({ page, perPage, filters })
  const [deleteDocuments, { isLoading: isDeleting }] =
    useDebugDeleteDocumentsMutation()

  const activeFilter = hasActiveFilter(filters)
  const items = data?.items ?? []
  const allSelected =
    items.length > 0 && items.every((item) => selectedIds.includes(item.id))

  const writeParams = (
    nextFilters: DebugDocumentFilters,
    nextPage: number,
    nextPerPage: number
  ) => {
    const params = new URLSearchParams()
    params.set('page', String(nextPage))
    params.set('perPage', String(nextPerPage))
    if (nextFilters.hash) {
      params.set('hash', nextFilters.hash)
    }
    nextFilters.nomDocuments.forEach((value) =>
      params.append('nomDocuments[]', value)
    )
    if (nextFilters.dateOperator) {
      params.set('dateOperator', nextFilters.dateOperator)
    }
    if (nextFilters.dateMonth) {
      params.set('dateMonth', nextFilters.dateMonth)
    }
    setSearchParams(params)
  }

  const onFilter = (event: FormEvent) => {
    event.preventDefault()
    setNotice(null)
    writeParams(
      {
        hash: draft.hash?.trim() || null,
        nomDocuments: draft.nomDocuments,
        dateOperator: draft.dateOperator,
        dateMonth: draft.dateMonth,
      },
      1,
      perPage
    )
  }

  const toggleId = (id: number) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    )
  }

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : items.map((item) => item.id))
  }

  const confirmDelete = async () => {
    try {
      const result = await deleteDocuments({
        ids: selectedIds,
        filters,
      }).unwrap()
      setNotice({ level: result.level, message: result.message })
      setSelectedIds([])
      setDialogOpen(false)
    } catch (deleteError) {
      setNotice({
        level: 'error',
        message: getDebugErrorMessage(deleteError),
      })
      setDialogOpen(false)
    }
  }

  return (
    <div className="iadocs-debug">
      <div className="debug-card">
        <div className="debug-header">
          <div>
            <h1>Debug - Documents S3 IADocs</h1>
            <div className="debug-meta">
              Suppression limitée aux documents sélectionnés de la page courante
              et correspondant aux filtres actifs.
            </div>
          </div>
        </div>

        <form className="debug-filters" onSubmit={onFilter}>
          <div className="debug-filters-documents">
            <div className="debug-field">
              <label className="debug-label" htmlFor="hash">
                Hash
              </label>
              <input
                className="debug-input"
                id="hash"
                value={draft.hash ?? ''}
                placeholder="Contient..."
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    hash: event.target.value,
                  }))
                }
              />
            </div>
            <DebugMultiSelect
              id="nomDocumentsDropdown"
              label="Type document"
              options={NOM_DOCUMENT_OPTIONS}
              selected={draft.nomDocuments}
              onChange={(nomDocuments) =>
                setDraft((current) => ({ ...current, nomDocuments }))
              }
            />
            <div className="debug-field">
              <label className="debug-label" htmlFor="dateOperator">
                Date création
              </label>
              <select
                className="debug-input"
                id="dateOperator"
                value={draft.dateOperator ?? ''}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    dateOperator: event.target.value || null,
                  }))
                }
              >
                <option value="">Opérateur</option>
                {DATE_OPERATOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="debug-field">
              <label className="debug-label" htmlFor="dateMonth">
                Mois
              </label>
              <input
                className="debug-input"
                id="dateMonth"
                type="month"
                value={draft.dateMonth ?? ''}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    dateMonth: event.target.value || null,
                  }))
                }
              />
            </div>
            <div className="debug-filters-actions">
              <button className="debug-btn debug-btn-primary" type="submit">
                Filtrer
              </button>
              <button
                className="debug-btn"
                type="button"
                onClick={() => {
                  setNotice(null)
                  setSearchParams(
                    new URLSearchParams({ perPage: String(perPage) })
                  )
                }}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </form>

        {notice && (
          <div className={`debug-flash ${notice.level}`}>{notice.message}</div>
        )}
        {isError && (
          <div className="debug-flash error">{getDebugErrorMessage(error)}</div>
        )}

        <div className="debug-bulk">
          <div className="debug-meta">
            {activeFilter
              ? 'Sélectionnez les documents à supprimer sur cette page.'
              : 'Activez au moins un filtre pour autoriser une suppression.'}
          </div>
          <button
            className="debug-btn debug-btn-danger"
            type="button"
            disabled={!activeFilter || selectedIds.length === 0 || isDeleting}
            onClick={() => setDialogOpen(true)}
          >
            Supprimer la sélection
          </button>
        </div>

        {isLoading ? (
          <div className="debug-empty">Chargement...</div>
        ) : items.length > 0 ? (
          <div className="debug-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Sélectionner tous les documents de la page"
                      checked={allSelected}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>ID</th>
                  <th>Date création</th>
                  <th>Date modification</th>
                  <th>Hash</th>
                  <th>Type document</th>
                  <th>MIME type</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Sélectionner le document ${item.id}`}
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleId(item.id)}
                      />
                    </td>
                    <td>{item.id}</td>
                    <td>{item.dateCreation ?? ''}</td>
                    <td>{item.dateModification ?? ''}</td>
                    <td className="debug-hash">{item.hash}</td>
                    <td>{item.nomDocument}</td>
                    <td>{item.mimeType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !isError ? (
          <div className="debug-empty">Aucun document trouvé.</div>
        ) : null}

        <DebugPagination
          page={data?.page ?? page}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          perPage={perPage}
          perPageOptions={DOCUMENT_PER_PAGE_OPTIONS}
          isFetching={isFetching && !isLoading}
          onPageChange={(nextPage) => writeParams(filters, nextPage, perPage)}
          onPerPageChange={(nextPerPage) =>
            writeParams(filters, 1, nextPerPage)
          }
        />
      </div>

      {dialogOpen && (
        <div className="debug-dialog-backdrop" role="presentation">
          <div
            className="debug-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deleteDialogTitle"
          >
            <h2 id="deleteDialogTitle">Confirmer la suppression</h2>
            <p className="debug-meta">
              {selectedIds.length} document(s) sélectionné(s) seront supprimés
              de S3 puis de la base si la suppression S3 réussit.
            </p>
            <div className="debug-dialog-actions">
              <button
                className="debug-btn"
                type="button"
                onClick={() => setDialogOpen(false)}
              >
                Annuler
              </button>
              <button
                className="debug-btn debug-btn-danger"
                type="button"
                disabled={isDeleting}
                onClick={confirmDelete}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
